import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, XCircle } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error | timeout

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (!active) return;
        if (res.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        // keep trying
      }
      tries += 1;
      if (tries >= MAX_TRIES) {
        if (active) setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-5">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-3xl border border-pink/25 bg-surface p-10 text-center"
        data-testid="payment-success-page"
      >
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto mb-6 h-12 w-12 text-pink animate-spin" />
            <h1 className="font-serif text-3xl text-ink">Confirming your payment…</h1>
            <p className="mt-3 text-muted font-light">One moment while we confirm with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.1 }}
              className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-pink"
            >
              <Check className="h-8 w-8 text-cream" />
            </motion.div>
            <h1 className="font-serif text-3xl text-ink" data-testid="payment-paid">Payment received</h1>
            <p className="mt-3 text-muted font-light">
              Thank you — your Fabrowz appointment is booked and paid. A confirmation email is on its way.
            </p>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle className="mx-auto mb-6 h-12 w-12 text-red-500" />
            <h1 className="font-serif text-3xl text-ink">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-3 text-muted font-light">
              {state === "timeout"
                ? "Your payment is taking a little longer than usual. If you were charged, your booking is confirmed — please check your email."
                : "We couldn't confirm your payment. You have not been charged. Please try booking again."}
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-home-link"
          className="mt-8 inline-block rounded-full bg-ink px-8 py-3.5 text-xs uppercase tracking-[0.2em] text-cream hover:bg-inkhover transition-colors"
        >
          Back to Fabrowz
        </Link>
      </motion.div>
    </div>
  );
}
